import React, { memo, useMemo } from 'react'
import tw, { TwStyle } from 'twin.macro'
import { Link } from 'react-router-dom'
import { Typography } from '@mui/material'
import { ADMIN_APP_GROUPS } from 'src/constants/adminApps'
import { GSIcon } from 'src/components/shared/GSIcon'
import { useInternalPermissions } from 'src/hooks/useInternalPermissions'
import { canAccessAdminApp } from 'src/utils/adminAppAccess'

// Groups share one grid, each spanning as many columns as it has apps (capped at 3), so a
// one-app group no longer leaves two empty columns beside it and every app fits above the fold.
// Twin needs static class strings, hence lookups rather than computed `col-span-${n}`.
const MAX_GROUP_COLUMNS = 3

const groupSpanCss: Record<number, TwStyle> = {
  1: tw`col-span-1`,
  2: tw`col-span-1 md:col-span-2`,
  3: tw`col-span-1 md:col-span-2 lg:col-span-3`,
}

const groupAppsGridCss: Record<number, TwStyle> = {
  1: tw`grid-cols-1`,
  2: tw`grid-cols-1 md:grid-cols-2`,
  3: tw`grid-cols-1 md:grid-cols-2 lg:grid-cols-3`,
}

const resolveGroupColumns = (appCount: number): number => {
  return Math.min(Math.max(appCount, 1), MAX_GROUP_COLUMNS)
}

const LauncherPageComponent = () => {
  const permissions = useInternalPermissions()

  // A group whose every app is hidden is dropped, so no empty heading is left behind.
  const visibleGroups = useMemo(() => {
    return ADMIN_APP_GROUPS.map((group) => {
      return {
        ...group,
        apps: group.apps.filter((app) => {
          return canAccessAdminApp(app, permissions)
        }),
      }
    }).filter((group) => {
      return group.apps.length > 0
    })
  }, [permissions])

  return (
    <main css={tw`flex-1 overflow-y-auto px-6 py-8`}>
      <div css={tw`mx-auto w-full max-w-5xl`}>
        <Typography variant="h1" css={tw`mb-6 text-shades-900`}>
          GigSafe Admin
        </Typography>

        <div css={tw`grid grid-cols-1 gap-x-4 gap-y-8 md:grid-cols-2 lg:grid-cols-3`}>
          {visibleGroups.map((group) => {
            const groupColumns = resolveGroupColumns(group.apps.length)

            return (
              <div
                key={group.label}
                css={[tw`flex flex-col`, groupSpanCss[groupColumns]]}
                data-testid={`launcher-group-${group.label}`}
              >
                <div
                  css={tw`mb-3 text-[11px] font-semibold uppercase tracking-[0.04em] text-shades-400`}
                >
                  {group.label}
                </div>
                <div css={[tw`grid flex-1 gap-4`, groupAppsGridCss[groupColumns]]}>
                  {group.apps.map((app) => {
                    // Mirrors the step cards on the client Home page (StepConfigItem): white
                    // surface, 16px radius, no border, a 44px round icon well and a caption
                    // status under the title.
                    return (
                      <Link
                        key={app.id}
                        to={app.path}
                        data-testid={`launcher-card-${app.id}`}
                        css={tw`flex h-full min-h-[80px] flex-col gap-2 rounded-2xl border border-solid border-transparent bg-shades-0 p-4 text-shades-1000 no-underline hover:border-shades-200`}
                      >
                        <div css={tw`flex flex-row items-center`}>
                          <div
                            css={tw`mr-4 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-shades-100`}
                          >
                            <GSIcon iconName={app.iconName} size={20} css={tw`text-shades-1000`} />
                          </div>
                          <div css={tw`flex flex-col`}>
                            <Typography variant="h5" css={tw`text-shades-900`}>
                              {app.title}
                            </Typography>
                            <Typography
                              variant="subtitle1"
                              css={app.isAvailable ? tw`text-success-500` : tw`text-shades-500`}
                            >
                              {app.isAvailable ? 'Ready' : 'Not built'}
                            </Typography>
                          </div>
                          <GSIcon
                            iconName="gs_chevron_right_small"
                            size={20}
                            css={tw`ml-auto shrink-0 text-shades-400`}
                            aria-hidden="true"
                          />
                        </div>
                        <Typography css={tw`text-sm text-shades-500`}>{app.description}</Typography>
                      </Link>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </main>
  )
}

export const LauncherPage = memo(LauncherPageComponent)
